import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useLoading } from '../../../contexts/LoadingContext';
import { useAuth } from '../../../contexts/AuthContext';
import apiClient from '../../../utils/apiClient';
import { ReactComponent as Logo } from '../../../assets/images/popashot_logo.svg';
import ErrorMessage from '../../common/ErrorMessage';
import LoadingButton from '../../common/LoadingButton';
import GoogleSignInButton from '../GoogleSignInButton';

const LoginStep = ({ onNavigateToSignup, onLoginSuccess }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const { startLoading, stopLoading } = useLoading();
    const { login, checkEmail } = useAuth();

    const handleEmailCheck = async (e) => {
        e.preventDefault();
        setError('');
        startLoading('emailCheck');

        try {
            const normalizedEmail = email.toLowerCase();
            const response = await checkEmail(normalizedEmail);
            
            if (response.exists) {
                if (!response.verified) {
                    // Trigger verification flow for unverified emails
                    try {
                        await apiClient.post('/api/users/resend-verification/', { email: normalizedEmail });
                        onNavigateToSignup(normalizedEmail, true); // Pass true for verification flow
                    } catch (error) {
                        setError('Failed to send verification email. Please try again.');
                    }
                } else if (!response.has_set_password) {
                    // Email is verified but password not set - go to reset password
                    onNavigateToSignup(normalizedEmail, false, false, true);
                } else if (!response.profile_setup_complete) {
                    // Email is verified and has password but profile incomplete
                    // Email is verified and has password but profile incomplete
                    setShowPassword(true);
                } else {
                    setShowPassword(true);
                }
            } else {
                onNavigateToSignup(normalizedEmail);
            }
        } catch (error) {
            setError('Unable to check email. Please try again.');
        } finally {
            stopLoading('emailCheck');
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        startLoading('login');

        try {
            // Always get the email from state, even if field is disabled
            const normalizedEmail = email.toLowerCase();
            console.log('Attempting login with:', { email: normalizedEmail, password: password ? '[PRESENT]' : '[MISSING]' }); // Debug log
            const response = await login(normalizedEmail, password);
            
            if (!response.user.profile_setup_complete) {
                onNavigateToSignup(normalizedEmail, false, false, false, true);  // Add flag for post-login profile setup
            } else {
                onLoginSuccess();
            }
        } catch (error) {
            console.error('Login error details:', error.response?.data || error);
            const responseData = error.response?.data;
            let message = 'Login failed. Please try again.';

            if (responseData) {
                if (typeof responseData === 'string') message = responseData;
                else if (responseData.non_field_errors) message = responseData.non_field_errors[0];
                else if (responseData.detail) message = responseData.detail;
                else {
                    const firstError = Object.values(responseData)[0];
                    message = Array.isArray(firstError) ? firstError[0] : firstError;
                }
            }

            setError(message);
        } finally {
            stopLoading('login');
        }
    };

    return (
        <div className="space-y-8 bg-transparent">
            <div className="flex items-center justify-center">
                <Logo className="w-48 h-auto" />
            </div>

            <div className="text-center">
                <h1>Sign in to your account</h1>
                <p className="mt-4">
                    Not a member?{' '}
                    <button
                        onClick={() => onNavigateToSignup(email || '')}
                        className="font-normal text-popashot-blue-light hover:text-popashot-blue border-none bg-transparent"
                    >
                        Sign up
                    </button>
                </p>
            </div>

            <form className="mt-8 space-y-6" onSubmit={!showPassword ? handleEmailCheck : handleLogin}>
                <GoogleSignInButton />

                <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-300" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-gray-50 text-gray-500">
                            or
                        </span>
                    </div>
                </div>
                <div className="rounded-md shadow-sm -space-y-px">
                    <div>
                        <label htmlFor="email" className="sr-only">
                            Email Address
                        </label>
                        <input
                            id="email"
                            name="email"
                            type="email"
                            required
                            className="appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                            placeholder="Email Address"
                            autoComplete="email"
                            spellCheck="false"
                            enterKeyHint="next"
                            aria-label="Email address"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={showPassword} 
                        />
                    </div>
                    
                    {showPassword && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                        >
                            <label htmlFor="password" className="sr-only">
                                Password
                            </label>
                            <input
                                id="password"
                                name="password"
                                type="password"
                                required
                                className="appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                                placeholder="Password"
                                autoComplete="current-password"
                                enterKeyHint="done"
                                aria-label="Password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoFocus
                            />
                            <p className="mt-2 text-sm">
                                <button 
                                    type="button"
                                    onClick={() => onNavigateToSignup(email, false, false, true)}
                                    className="text-popashot-blue-light hover:text-popashot-blue"
                                >
                                    Forgot your password?
                                </button>
                            </p>
                        </motion.div>
                    )}
                </div>

                <ErrorMessage error={error} />

                <LoadingButton
                    type="submit"
                    loadingKey={showPassword ? 'login' : 'emailCheck'}
                    className="btn-primary w-full"
                >
                    {showPassword ? 'Sign in' : 'Continue'}
                </LoadingButton>
            </form>
        </div>
    );
};

export default LoginStep;
